<template>
    <div class="template-items-container" :class="[$mq, { 'is-editing': !!(editingItem || createSlotItem) }]">
        <TemplateItemEdit
            v-if="editingItem"
            :item="editingItem"
            :template-id="templateId"
            :config-id="configId"
            :location-id="locationId"
            :template-type="templateData ? templateData.template_type : null"
            :template-config="templateData ? templateData.config : {}"
            :create-mode="false"
            @close="editingItem = null"
            @saved="onItemSaved"
            @deleted="onItemDeleted"
        />
        <TemplateItemEdit
            v-else-if="createSlotItem"
            :item="createSlotItem"
            :template-id="templateId"
            :config-id="configId"
            :location-id="locationId"
            :template-type="templateData ? templateData.template_type : null"
            :template-config="templateData ? templateData.config : {}"
            :create-mode="true"
            :section-title-item-id="createSectionTitleItemId"

            @close="onCloseCreateItem"
            @saved="onItemSaved"
            @deleted="onItemDeleted"
        />
        <template v-else>
            <div class="header-section">
                <div class="header-row">
                    <button class="back-button" @click="$emit('back')" :title="$t('config.templates.items.back')">
                        <i class="i-andy-chevron-left i-andy-m"></i>
                    </button>
                    <h2 class="template-title">{{ templateName || $t('config.templates.items.loading') }}</h2>
                </div>
                <div v-if="templateData" class="info-message">
                    <span class="info-icon-wrapper">
                        <i class="i-andy-info-main i-andy-m"></i>
                    </span>
                    <span class="info-text">
                        {{ canEditAnyItem ? $t('config.templates.items.info_message') : $t('config.templates.items.info_message_no_editable') }}
                    </span>
                </div>
            </div>

            <div v-if="loading" class="loading-container">
                <div class="loading-spinner"></div>
            </div>

            <div v-else-if="errorMessage" class="error-container">
                <p class="error-message">{{ errorMessage }}</p>
            </div>

            <template v-else-if="templateData">
                <div v-if="sections.length > 3 && showGroupedSections" class="section minimize">
                    <div v-if="!allSectionsCollapsed" class="section-title" @click="toggleAllSections">
                        <button class="section-button"></button>
                        <span>{{ $t('audit.actions.collapse_all') }}</span>
                    </div>
                    <div v-else class="section-title expand" @click="toggleAllSections">
                        <button class="section-button"></button>
                        <span>{{ $t('audit.actions.expand_all') }}</span>
                    </div>
                </div>

                <div v-if="sections.length > 0 && showGroupedSections" class="sections-list">
                    <div
                        v-for="(section, sectionIndex) in sections"
                        :key="section.section_id"
                        class="section-group"
                    >
                        <div class="section-header">
                            <button
                                type="button"
                                class="section-toggle-btn"
                                :aria-label="isSectionCollapsed(section.section_id) ? $t('config.templates.items.expand_section') : $t('config.templates.items.collapse_section')"
                                @click="toggleSection(section.section_id)"
                            >
                                <span class="toggle-icon">{{ isSectionCollapsed(section.section_id) ? '+' : '\u2212' }}</span>
                            </button>
                            <h3 class="section-title">{{ sectionDisplayName(section, sectionIndex) }}</h3>
                        </div>
                        <div v-if="!isSectionCollapsed(section.section_id)" class="items-list">
                            <div
                                v-for="item in section.items"
                                :key="item.id"
                                class="item-card"
                                :class="{ 'not-applicable': itemEffectiveApply(item) === 0 }"
                            >
                                <div class="item-icon">
                                    <i :class="['i-andy-m', itemEffectiveApply(item) === 0 ? 'i-andy-error' : itemTypeIcon(item.type)]"></i>
                                </div>
                                <div class="item-content">
                                    <div class="item-info">
                                        <span class="item-title">{{ itemDisplayFullTitle(item) }}</span>
                                        <span v-if="itemDisplayEquipmentId(item)" class="item-id">{{ itemDisplayEquipmentId(item) }}</span>
                                        <span v-if="itemEffectiveApply(item) === 0" class="item-na-desc">{{ $t('config.templates.items.item_no_visible_desc') }}</span>
                                    </div>
                                    <button
                                        v-if="canOpenEditItem(item)"
                                        class="action-btn"
                                        @click="openEditItem(item)"
                                        :title="$t('config.templates.items.edit')"
                                        :aria-label="$t('config.templates.items.edit')"
                                    >
                                        <i class="i-andy-edit i-andy-m"></i>
                                    </button>
                                </div>
                            </div>
                            <button
                                v-if="canAddItem"
                                type="button"
                                class="add-item-btn"
                                @click="openCreateItem(section.section_id)"
                                :aria-label="$t('config.templates.items.add_item_form.title')"
                            >
                                <i class="add-item-btn__icon i-andy-add i-andy-m" aria-hidden="true"></i>
                                <span class="add-item-btn__text">{{ $t('config.templates.items.add_item_form.title') }}</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div v-else-if="sections.length > 0" class="items-list">
                    <div
                        v-for="item in flatItems"
                        :key="item.id"
                        class="item-card"
                        :class="{ 'not-applicable': itemEffectiveApply(item) === 0 }"
                    >
                        <div class="item-icon">
                            <i :class="['i-andy-m', itemEffectiveApply(item) === 0 ? 'i-andy-error' : itemTypeIcon(item.type)]"></i>
                        </div>
                        <div class="item-content">
                            <div class="item-info">
                                <span class="item-title">{{ itemDisplayFullTitle(item) }}</span>
                                <span v-if="itemDisplayEquipmentId(item)" class="item-id">{{ itemDisplayEquipmentId(item) }}</span>
                                <span v-if="itemEffectiveApply(item) === 0" class="item-na-desc">{{ $t('config.templates.items.item_no_visible_desc') }}</span>
                            </div>
                            <button
                                v-if="canOpenEditItem(item)"
                                class="action-btn"
                                @click="openEditItem(item)"
                                :title="$t('config.templates.items.edit')"
                                :aria-label="$t('config.templates.items.edit')"
                            >
                                <i class="i-andy-edit i-andy-m"></i>
                            </button>
                        </div>
                    </div>
                    <button
                        v-if="canAddItem"
                        type="button"
                        class="add-item-btn"
                        @click="openCreateItem(null)"
                        :aria-label="$t('config.templates.items.add_item_form.title')"
                    >
                        <i class="add-item-btn__icon i-andy-add i-andy-m" aria-hidden="true"></i>
                        <span class="add-item-btn__text">{{ $t('config.templates.items.add_item_form.title') }}</span>
                    </button>
                </div>

                <EmptyPage
                    v-else
                    :title="$t('config.templates.items.empty_title')"
                    :description="$t('config.templates.items.empty_description')"
                />
            </template>
        </template>
    </div>
</template>

<script>
/* global logError */
import EmptyPage from '@/components/layout/EmptyPage.vue'
import TemplateItemEdit from '@/components/domain/config/TemplateItemEdit.vue'
import { getFirstCustomRecord, canOpenEditItem, getItemTypeIcon } from '@/utils/templateCustom'
import { normalizeFlagToBool } from '@/utils/flag'

export default {
    name: 'TemplateItems',
    components: {
        EmptyPage,
        TemplateItemEdit
    },
    props: {
        templateId: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            templateData: null,
            loading: false,
            errorMessage: null,
            editingItem: null,
            createSlotItem: null,
            createSectionTitleItemId: null,
            collapsedSections: {}
        }
    },
    computed: {
        templateName() {
            return this.templateData ? this.templateData.template_name : ''
        },
        sections() {
            if (!this.templateData || !Array.isArray(this.templateData.sections)) return []
            return this.templateData.sections.filter((s) => Array.isArray(s.items) && s.items.length > 0)
        },
        flatItems() {
            return this.sections.reduce((acc, section) => acc.concat(section.items || []), [])
        },
        showGroupedSections() {
            // Si solo hay una "sección" sin nombre, mostrar listado plano (no separar por secciones)
            if (this.sections.length <= 1) {
                const first = this.sections[0]
                if (!first) return false
                const name = first.name ? String(first.name).trim() : ''
                return name !== ''
            }
            return true
        },
        configId() {
            // Siempre string: `null` en :config-id anula el default del hijo ('auto') y rompe isAuto en Vue.
            if (!this.templateData || !this.templateData.config) return 'auto'
            const raw = this.templateData.config.id
            if (raw == null) return 'auto'
            const s = String(raw).trim()
            return s === '' ? 'auto' : s
        },
        locationId() {
            const location = this.$store.getters['loginUser/getLocalLocation']
            return location && location.id ? location.id : null
        },
        canEditAnyItem() {
            return this.flatItems.some((item) => this.canOpenEditItem(item))
        },
        canAddItem() {
            const cfg = this.templateData && this.templateData.config ? this.templateData.config : {}
            return normalizeFlagToBool(cfg.create_items) || normalizeFlagToBool(cfg.conf_create)
        },
        allSectionsCollapsed() {
            return this.sections.length > 0 && this.sections.every((s) => !!this.collapsedSections[s.section_id])
        }
    },
    methods: {
        canOpenEditItem(item) {
            const cfg = this.templateData && this.templateData.config ? this.templateData.config : {}
            const canEdit = normalizeFlagToBool(cfg.edit_items) || normalizeFlagToBool(cfg.conf_edit)
            const canCreate = normalizeFlagToBool(cfg.create_items) || normalizeFlagToBool(cfg.conf_create)
            return canOpenEditItem(item, canEdit, canCreate)
        },
        getCustomRecord(item) {
            return getFirstCustomRecord(item ? item.custom : null)
        },
        itemDisplayTitle(item) {
            const rec = this.getCustomRecord(item)
            if (rec && rec.title != null && String(rec.title).trim() !== '') return String(rec.title)
            if (typeof item.custom === 'string' && item.custom.trim() !== '') return item.custom
            return item && item.title ? item.title : ''
        },
        itemDisplayDescription(item) {
            const rec = this.getCustomRecord(item)
            if (rec && rec.description != null && String(rec.description).trim() !== '') return String(rec.description)
            if (item && item.description != null && String(item.description).trim() !== '') return String(item.description)
            return ''
        },
        itemDisplayFullTitle(item) {
            const title = this.itemDisplayTitle(item)
            const desc = this.itemDisplayDescription(item)
            return desc ? `${title} | ${desc}` : title
        },
        itemDisplayEquipmentId(item) {
            const rec = this.getCustomRecord(item)
            if (rec && rec.equipment_id != null && String(rec.equipment_id).trim() !== '') return String(rec.equipment_id)
            if (item && item.equipment_id != null && String(item.equipment_id).trim() !== '') return String(item.equipment_id)
            return ''
        },
        itemEffectiveApply(item) {
            const rec = this.getCustomRecord(item)
            const raw = rec && rec.apply != null ? rec.apply : (item ? item.apply : 1)
            const n = Number(raw)
            return Number.isNaN(n) ? 1 : n
        },
        sectionDisplayName(section, index) {
            const name = section.name ? section.name.trim() : ''
            if (name) return name
            return this.$t('config.templates.items.default_section_name', { number: index + 1 })
        },
        toggleSection(sectionId) {
            this.$set(this.collapsedSections, sectionId, !this.collapsedSections[sectionId])
        },
        toggleAllSections() {
            const collapse = !this.allSectionsCollapsed
            this.sections.forEach((s) => {
                this.$set(this.collapsedSections, s.section_id, collapse)
            })
        },
        isSectionCollapsed(sectionId) {
            return !!this.collapsedSections[sectionId]
        },
        itemTypeIcon(type) {
            return getItemTypeIcon(type)
        },
        async loadItems() {
            this.loading = true
            this.errorMessage = null

            try {
                const data = await this.$store.dispatch('templates/loadTemplateItems', {
                    templateId: this.templateId,
                    // locationId se mantiene por compatibilidad, pero ya no es requerido para listar desde storage
                    locationId: this.locationId
                })

                if (data) {
                    this.templateData = data
                } else {
                    this.errorMessage = this.$t('config.templates.items.error_load')
                }
            } catch (error) {
                logError('Error cargando items de plantilla:', error)
                this.errorMessage = this.$t('config.templates.items.error_load')
            } finally {
                this.loading = false
            }
        },
        openEditItem(item) {
            if (!this.canOpenEditItem(item)) return
            this.createSlotItem = null
            this.createSectionTitleItemId = null
            this.editingItem = item
            this.$emit('edit-item', item)
        },
        openCreateItem(sectionId) {
            if (!this.canAddItem) return
            let slot = null
            if (sectionId != null && sectionId !== 'default') {
                const section = this.sections.find((s) => s.section_id === sectionId)
                const items = section && Array.isArray(section.items) ? section.items : []
                if (items.length > 0) {
                    slot = items[0]
                }
            }
            if (!slot && this.flatItems.length > 0) {
                slot = this.flatItems[0]
            }
            if (!slot) {
                this.$snackbar.error({
                    message: this.$t('config.templates.items.add_item_form.no_slot_available'),
                    duration: 5000,
                    closeable: true
                })
                return
            }
            this.editingItem = null
            this.createSlotItem = slot
            this.createSectionTitleItemId =
                sectionId != null && sectionId !== 'default' ? sectionId : null
        },
        onCloseCreateItem() {
            this.createSlotItem = null
            this.createSectionTitleItemId = null
        },
        onItemSaved({ created, displayTitle } = {}) {
            this.editingItem = null
            this.createSlotItem = null
            this.createSectionTitleItemId = null
            this.loadItems()
            const label = (displayTitle || '').trim()
            let message = this.$t('config.templates.items.save_success_message')
            if (label) {
                if (created === true) {
                    message = this.$t('config.templates.items.create_success_message_with_title', { title: label })
                } else if (created === false) {
                    message = this.$t('config.templates.items.update_success_message_with_title', { title: label })
                }
            }
            this.$snackbar.success({
                message,
                duration: 4000,
                closeable: true
            })
        },
        onItemDeleted({ item, displayTitle } = {}) {
            this.editingItem = null
            this.createSlotItem = null
            this.createSectionTitleItemId = null
            this.loadItems()
            const label = (displayTitle || (item && item.title) || '').trim()
            const message = label
                ? this.$t('config.templates.items.delete_success_message_with_title', { title: label })
                : this.$t('config.templates.items.delete_success_message_no_title')
            this.$snackbar.success({
                message,
                duration: 4000,
                closeable: true
            })
        }
    },
    created() {
        this.loadItems()
    }
}
</script>

<style lang="scss" scoped>
.template-items-container {
    padding: 8px;
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;

    &.is-editing {
        height: 100%;
        min-height: 0;
        align-items: center;
    }

    .header-section {
        display: flex;
        flex-direction: column;
        gap: 8px;

        .header-row {
            @include display-flex();
            @include align-items(center);
            gap: 8px;
        }

        .back-button {
            @include interaction();
            @include display-flex();
            @include align-items(center);
            @include justify-content(center);
            flex-shrink: 0;
            width: 40px;
            height: 40px;
            background: #fff;
            border: 1px solid $neutro-t30;
            @include border-radius(4px);
            padding: 0;
            cursor: pointer;
            color: $neutro-s70;

            &:hover {
                color: $main;
                border-color: $main;
            }
        }

        .template-title {
            display: -webkit-box;
            -webkit-box-orient: vertical;
            -webkit-line-clamp: 3;
            flex: 1 0 0;
            overflow: hidden;
            color: #2A2A2A;
            text-overflow: ellipsis;
            font-size: 18px;
            font-style: normal;
            font-weight: 700;
            line-height: normal;
            margin: 0;
            padding: 0;
        }

        .info-message {
            @include display-flex();
            @include align-items(center);
            gap: 10px;
            padding: 12px 16px;
            margin: 0;
            background: #EAE8E4;
            border: 1px solid $neutro-t30;
            @include border-radius(4px);
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);

            .info-icon-wrapper {
                @include display-flex();
                @include align-items(center);
                @include justify-content(center);
                flex-shrink: 0;

                .i-andy-info-main {
                    color: $neutro-s60;
                }
            }

            .info-text {
                color: #444;
                font-size: 14px;
                font-style: normal;
                font-weight: 400;
                line-height: 18px;
            }
        }
    }

    .loading-container {
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 40px 0;

        .loading-spinner {
            width: 40px;
            height: 40px;
            border: 3px solid $neutro-t30;
            border-top-color: $main;
            @include border-radius(50%);
            animation: spin 0.8s linear infinite;
        }
    }

    .error-container {
        padding: 20px;
        background-color: $color-warning-100;
        border-radius: 4px;

        .error-message {
            color: $color-warning-800;
            font-family: $text;
            margin: 0;
        }
    }

    .sections-list {
        display: flex;
        flex-direction: column;
        gap: 20px;
    }

    .section.minimize {
        opacity: 0.85;
        margin-bottom: 0;
        margin-top: 0;

        &:hover {
            opacity: 1;
        }

        .section-title {
            @include font-size(s);
            font-family: $text;
            color: $main-dark;
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 8px;
            background: transparent;
            min-height: unset;
            height: auto;
            padding: 0;
            border-radius: 0;
            text-transform: none;
            justify-content: flex-start;

            .section-button {
                @include background($color: $main-t80, $image: img('less_primary.svg'), $size: 10px);
                @include border-radius(2px);
                width: 18px;
                height: 18px;
                padding: 4px;
                cursor: pointer;
                flex-shrink: 0;
            }

            &.expand .section-button {
                @include background($color: $main-t80, $image: img('more_primary.svg'), $size: 10px);
            }
        }
    }

    .section-group {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .section-header {
        @include display-flex();
        @include align-items(center);
        gap: 8px;
    }

    .section-toggle-btn {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        flex-shrink: 0;
        width: 30px;
        height: 30px;
        aspect-ratio: 1 / 1;
        padding: 0;
        border: none;
        @include border-radius(4px);
        background: $color-secondary-50;
        cursor: pointer;
        transition: background 0.2s ease;

        .toggle-icon {
            @include font-size(ml);
            font-family: $text;
            color: $color-secondary-500;
            line-height: 1;
        }

        &:hover {
            background: $color-secondary-100;
        }
    }

    .section-title {
        @include display-flex();
        @include align-items(center);
        @include justify-content(center);
        min-height: 40px;
        height: auto;
        padding: 10px 12px;
        margin: 0;
        flex: 1 0 0;
        @include border-radius(4px);
        background: $color-oat-200;
        color: #2A2A2A;
        text-align: center;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: normal;
        text-transform: uppercase;
    }

    .items-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    .add-item-btn {
        display: flex;
        padding: 8px;
        flex-direction: row;
        align-items: center;
        gap: 8px;
        align-self: stretch;
        margin: 0 0 12px 0;
        border: none;
        border-radius: 4px;
        background: var(--color-secondary-50, $color-secondary-50);
        font-family: $text-bold;
        cursor: pointer;
        transition: background 0.2s ease;

        .add-item-btn__icon {
            display: flex;
            padding: 4px;
            justify-content: center;
            align-items: center;
            gap: 4px;
            border-radius: 4px;
            border: 1px solid var(--color-Neutral-300, $color-neutral-300);
            background: var(--color-Neutral-White, #fff);
            flex-shrink: 0;
            font-size: 16px;
            line-height: 1;
        }

        .add-item-btn__text {
            color: var(--color-secondary-500, $color-secondary-500);
            font-family: $text-bold;
            font-size: 16px;
            font-style: normal;
            font-weight: 700;
            line-height: 18px;
        }

        &:focus-visible {
            outline: 2px solid $color-secondary-500;
            outline-offset: 2px;
        }
    }

    .item-card {
        display: flex;
        align-items: stretch;
        gap: 0;
        align-self: stretch;
        border-radius: 4px;
        border-left: 4px solid $color-neutral-300;
        background: #fff;
        transition: all 0.2s ease;

        &.not-applicable {
            .item-info .item-title,
            .item-info .item-id,
            .item-info .item-na-desc {
                color: var(--color-Neutral-300, #B8B8B8);
            }
        }

        .item-icon {
            @include display-flex();
            @include align-items(center);
            @include justify-content(center);
            flex-shrink: 0;
            padding: 12px 4px;
            color: $color-neutral-300;

            i,
            .i-andy-format-temperature {
                color: $color-neutral-300;
                font-size: 24px;
                width: 24px;
                height: 24px;
            }
        }

        .item-content {
            flex: 1;
            display: flex;
            padding: 12px 8px 12px 4px;
            flex-direction: row;
            justify-content: space-between;
            align-items: center;
            gap: 16px;
            align-self: stretch;
            min-width: 0;
        }

        .item-info {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
            gap: 8px;
            min-width: 0;

            .item-title {
                color: #2A2A2A;
                font-size: 18px;
                font-style: normal;
                font-weight: 700;
                line-height: normal;
                display: -webkit-box;
                -webkit-line-clamp: 2;
                -webkit-box-orient: vertical;
                overflow: hidden;
                text-overflow: ellipsis;
            }

            .item-id {
                display: -webkit-box;
                -webkit-box-orient: vertical;
                -webkit-line-clamp: 1;
                align-self: stretch;
                overflow: hidden;
                color: #444;
                text-overflow: ellipsis;
                font-size: 14px;
                font-style: normal;
                font-weight: 400;
                line-height: normal;
            }

            .item-na-desc {
                @include font-size(xs);
                font-family: $text;
                color: $neutro-s50;
            }
        }

        .action-btn {
            @include interaction();
            flex-shrink: 0;
            width: 32px;
            height: 32px;
            padding: 8px;
            border-radius: 4px;
            border: none;
            background: transparent;
            cursor: pointer;
            @include display-flex();
            @include align-items(center);
            @include justify-content(center);
            color: $neutro-s60;

            &:hover:not(:disabled) {
                background-color: $neutro-t30;
                color: $main;
            }

            &:disabled {
                opacity: 0.45;
                cursor: not-allowed;
                background: transparent;
            }
        }
    }
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

.template-items-container {
    ::v-deep .empty-container {
        height: auto;
        min-height: 0;
        padding-top: 16px;

        .empty-page-content {
            .content-message {
                width: 100%;
                max-width: 100%;
            }
        }
    }
}

.template-items-container.portrait {
    padding: 0;
}
</style>
